import type { BaseHeaderContent } from 'src/components/layout/redesign/header-mobile/header-menu';
import type { HeaderMobileSubMenuProps } from 'src/components/layout/redesign/header-mobile/header-sub-menu';
import type { Styling } from 'src/context/styling';
import { RedesignButtonStyle } from 'src/enums/button-style';

export const headerMobileSubMenuWrapperDelay = 400;

type SubMenuProps = Pick<HeaderMobileSubMenuProps, 'level' | 'title' | 'description' | 'links'> & {
  id: string;
};

export const idForMenu = (level1Index?: number | null, level2Index?: number | null) => {
  if (level1Index === null || level1Index === undefined) {
    return 'header';
  }
  if (level2Index === null || level2Index === undefined) {
    return `header-${level1Index}`;
  }
  return `header-${level1Index}-${level2Index}`;
};

export const mapHeaderContentToSubMenus = (
  headerContent: BaseHeaderContent | null,
  onSelectLevel1Index: (index: number) => void,
  onSelectLevel2Index: (index: number) => void
): SubMenuProps[] | null => {
  if (!headerContent) {
    return null;
  }

  const level1Menu: SubMenuProps = {
    id: idForMenu(),
    level: 1,
    links: (headerContent.headerMenuItems ?? []).map(
      ({ name, description, levelTwoMenuItems, link }, index) => {
        const shouldOpenLevel2Menu = !!description?.trim() || !!levelTwoMenuItems?.length;

        return shouldOpenLevel2Menu
          ? {
              name,
              onClick: () => onSelectLevel1Index(index),
            }
          : {
              name,
              link: link ?? '',
            };
      }
    ),
  };

  const level2Menus: SubMenuProps[] = (headerContent.headerMenuItems ?? []).map(
    (level1Menu, level1Index) => ({
      id: idForMenu(level1Index),
      level: 2,
      title: level1Menu.name,
      description: level1Menu.description,
      links: [
        ...(level1Menu.link
          ? [
              {
                name: headerContent.headerOtherText?.overviewText ?? '',
                link: level1Menu.link,
              },
            ]
          : []),
        ...(level1Menu.levelTwoMenuItems ?? []).map(
          ({ name, link, levelThreeMenuItems }, level2Index) =>
            levelThreeMenuItems?.length
              ? {
                  name,
                  onClick: () => onSelectLevel2Index(level2Index),
                }
              : {
                  name,
                  link,
                }
        ),
      ],
    })
  );

  const level3Menus: SubMenuProps[] = (headerContent.headerMenuItems ?? []).flatMap(
    (level1Menu, level1Index) =>
      (level1Menu.levelTwoMenuItems ?? []).flatMap((level2Menu, level2Index) => ({
        id: idForMenu(level1Index, level2Index),
        level: 3,
        title: level2Menu.name,
        links: [
          {
            name: headerContent.headerOtherText?.overviewText ?? '',
            link: level2Menu.link,
          },
          ...(level2Menu.levelThreeMenuItems ?? []),
        ],
      }))
  );

  return [level1Menu, ...level2Menus, ...level3Menus];
};

export const createHeaderButtonBorderStyling = (styling: Styling, buttonStyle?: string | null) => {
  switch (buttonStyle) {
    case RedesignButtonStyle.WHITE_WITH_PLUM_FONT:
      return {
        'border': `1px solid ${styling.colors.camelot}`,
        '&:focus': {
          border: `1px solid ${styling.colors.camelot}`,
        },
      };
    case RedesignButtonStyle.WHITE_WITH_GREEN_FONT:
    case RedesignButtonStyle.TRANSPARENT_WITH_PLUM_FONT:
      return {
        'border': `1px solid ${styling.colors.grayOlive}`,
        '&:focus': {
          border: `1px solid ${styling.colors.grayOlive}`,
        },
      };
    default:
      return null;
  }
};
